import React, {useContext, useEffect, useState} from "react";
import {filter, find, get, keys, map} from 'lodash';
import {i18n} from "../../../Utils/i18n/i18n";
import './ImportUsers.scss'
import {UploadButton} from "../../Form/Button/UploadButton";
import ImportIcon from '@mui/icons-material/ImportExport';
import {buttonColors} from "../../Pickers/InputTheme";
import {importUsersFromFile} from "../../../Utils/excel";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {FeatureFlags, MessageTypes} from "../../../Utils/Constants";
import {FormControlLabel, Switch} from "@mui/material";
import {MessageTypeSelectionButtons} from "../../Notifications/MessageTypeSelectionButtons/MessageTypeSelectionButtons";
import {CompanyReactContext} from "../../../contexts/CompanyContext";
import {useUserAdminOrgs} from "../../Hooks/UseUserAdminOrgs";
import {MultiSelectAutocomplete} from "../../Form/AutoComplete/AutoComplete";
import {ImportUsersReactContext} from "./ImportUsersModalContext";
import {UserReactContext} from "../../../contexts/UserContext";
import {LocalPopover} from "../../../Utils/Popover/Popover";
import {DataTable} from "../../DataTable/DataTable";
import { ScrollableListCell } from "../../../Utils/ScrollableListCell/ScrollableListCell";
import {formatPhoneForDisplay} from "../../../Utils/functions";
import {PhoneCell} from "../../../Utils/PhoneCell/PhoneCell";

export const ImportUsers = ({
                                onlyAddOrgsWithoutRemoving = true,
                                isRebootUsers = false
                            }) => {

    const {adminOrganizationOptions} = useUserAdminOrgs({})
    const [selectedOrganizations, setSelectedOrganizations] = useState([])
    const [isProcessingFile, setIsProcessingFile] = useState(false)
    const [usersToUpload, setUsersToUpload] = useState()
    const [isLoading, setIsLoading] = useState(false)
    const [errors, setErrors] = useState()
    const [sendWelcomeMessage, setSendWelcomeMessage] = React.useState(false)
    const [selectedMessagingTypes, setSelectedMessagingTypes] = React.useState([MessageTypes.SMS])
    const companyContext = useContext(CompanyReactContext)
    const [processedFile, setProcessedFile] = useState()
    const importUsersContext = React.useContext(ImportUsersReactContext)

    useEffect(() => {
        let sendWelcomeMessageData;
        if (sendWelcomeMessage) {
            sendWelcomeMessageData = {
                messageTypes: selectedMessagingTypes
            }
        }
        const data = {
            organizationIds: map(selectedOrganizations, 'value'),
            users: map(keys(usersToUpload), key => usersToUpload[key]),
            sendWelcomeMessage: sendWelcomeMessageData,
            selectedMessagingTypes: selectedMessagingTypes,
            onlyAddOrgsWithoutRemoving,
            errors,
            isProcessingFile,
            isMissingSourceOfOrgs: isMissingSourceOfOrgs(),
            isThereTwoSourcesForOrgs: isThereTwoSourcesForOrgs(),
            isRebootUsers
        }
        importUsersContext.setUsersToUploadDataFunc(data)
        importUsersContext.setIsRebootUsersFunc(isRebootUsers)
    }, [usersToUpload, selectedOrganizations, sendWelcomeMessage, selectedMessagingTypes, onlyAddOrgsWithoutRemoving, errors, isProcessingFile]);


    const onSelectOrgsChange = async (selectedOrganizations) => {
        setIsLoading(true)
        setSelectedOrganizations(selectedOrganizations)
        setIsLoading(false)
    }

    const createUsersFromFile = async ({target}) => {
        const file = target.files[0]
        if (file) {
            setProcessedFile(file)
            await processSelectedFile(file)
        }
        target.value = ''
    }
    const processSelectedFile = async (file) => {
        if (!file) return
        
        setIsProcessingFile(true)
        try {
            const dataRes = await importUsersFromFile({
                file,
                selectedMessagingTypes,
                sendWelcomeMessage,
                companyCountryCode: companyContext.countryCode
            })
            setUsersToUpload(dataRes.data)
            setErrors(dataRes.errors)
        } catch (error) {
            // Handle validation errors (object with errors and data properties)
            if (error && error.errors && error.data) {
                setUsersToUpload(error.data)
                setErrors(error.errors)
            } 
            // Handle general errors (Error object with message)
            else if (error && error.message) {
                alert(i18n.translate(error.message))
                setUsersToUpload(null)
                setErrors({})
            }
            // Handle unexpected errors
            else {
                alert(i18n.translate('errors.generalServerError'))
                setUsersToUpload(null)
                setErrors({})
            }
        } finally {
            setIsProcessingFile(false)
        }

    }
    // Re-process file when welcome message settings change
    useEffect(() => {
        if (processedFile) {
            processSelectedFile(processedFile)
        }
    }, [sendWelcomeMessage, selectedMessagingTypes]);



    const onSendWelcomeMessageChange = (e) => {
        setSendWelcomeMessage(e.target.checked)
    }
    const onSelectedMessagingTypeChange = (messagingType) => {
        //if it is the only one selected do not remove it

        if (selectedMessagingTypes.includes(messagingType) && selectedMessagingTypes.length > 1) {
            setSelectedMessagingTypes(selectedMessagingTypes.filter((type) => type !== messagingType))
        } else {
            let messagingTypes = [...selectedMessagingTypes, messagingType]
            //uniq array
            messagingTypes = messagingTypes.filter((value, index, self) => self.indexOf(value) === index)
            setSelectedMessagingTypes(messagingTypes)
        }
    }

    const getFilteredOrgs = ({query}) => {
        return filter(adminOrganizationOptions, org => org.text.toLowerCase().includes(query.toLowerCase()))
    }

    const isThereTwoSourcesForOrgs = () => {
        if (usersToUpload) {
            const userWithOrgs = find(usersToUpload, user => get(user, 'organizations.length'))
            if (userWithOrgs && selectedOrganizations && selectedOrganizations.length) {
                return true
            }
        }
        return false
    }
    const isMissingSourceOfOrgs = () => {
        // No users uploaded yet - orgs source is missing
        if (!usersToUpload) {
            return true
        }

        // Check if any user has orgs from file
        const hasOrgsFromFile = find(usersToUpload, user => get(user, 'organizations.length'))
        const hasOrgsFromDropdown = selectedOrganizations && selectedOrganizations.length > 0

        // Valid: orgs from file OR orgs from dropdown (but not both - checked by isThereTwoSourcesForOrgs)
        // Invalid: no orgs from either source
        if (!hasOrgsFromFile && !hasOrgsFromDropdown) {
            return true
        }

        return false
    }


    return <div className="importUsers">
        {(isLoading || importUsersContext.isUploading || isProcessingFile) && <LoadingSpinner/>}
        <div className="selectWrapper">
            <div className="selectTitle">
                <div>{onlyAddOrgsWithoutRemoving ? i18n.translate('importUsersModal.selectOrganizationsToAdd') : i18n.translate('importUsersModal.selectOrganization')}</div>
                <LocalPopover info={onlyAddOrgsWithoutRemoving ? i18n.translate('importUsersModal.newUsersWillBeCreatedAndOrganizationsWillBeAddedToAllUsers') : i18n.translate('importUsersModal.newUsersWillBeCreatedAndOrganizationsWillBeOverride')}></LocalPopover>
            </div>
            <div className="selectBoxRowWrapper">
                <div className="organizationsSelectWrapper">
                    {adminOrganizationOptions && adminOrganizationOptions.length ? <MultiSelectAutocomplete
                        selectAllText={i18n.translate('editAdminUserModal.selectAllOrgs')}
                        enableSelectAll
                        label={i18n.translate('editAdminUserModal.selectOrganizationalUnits')}
                        fetchOptions={getFilteredOrgs}
                        initialSelectedValues={selectedOrganizations}
                        onSelectionChange={onSelectOrgsChange}/> : ''}
                </div>
                <UploadButton color={buttonColors.GREY}
                              accept=".csv,.xlsx,.xls"
                              disabled={importUsersContext.isUploading || isProcessingFile}
                              icon={<ImportIcon/>}
                              onInputChange={createUsersFromFile}
                              uniqId={"upload-1"}
                              text={i18n.translate('OrganizationsPage.importUsersFile')}
                />
            </div>
        </div>
        <div className="errorContainer">
            {map(keys(errors).slice(0, 2), (errorRowIndex, index) => {
                return <div key={`error-${index}`} className="error">
                    {i18n.translate('importUsersModal.errors.missingMandatoryField', {context: {row: parseInt(errorRowIndex) + 1}})} {map(keys(errors[errorRowIndex]), attr => `${i18n.translate(`importUsersModal.users.${attr}`)}`).join(', ')}
                </div>
            })}
            {isThereTwoSourcesForOrgs() && <div className="error">
                {onlyAddOrgsWithoutRemoving ? i18n.translate('importUsersModal.errors.removeOrgsFromFile') : i18n.translate('importUsersModal.errors.twoSourcesForOrgs')}
            </div>}
            {isMissingSourceOfOrgs() && <div className="error">
                {onlyAddOrgsWithoutRemoving ? i18n.translate('importUsersModal.errors.missingSourceForOrgsSelectFromMultiSelect') : i18n.translate('importUsersModal.errors.missingSourceForOrgs')}
            </div>}
        </div>
        <div className="welcomeMessageSend">
            {companyContext.featureFlags[FeatureFlags.SEND_WELCOME_MESSAGE] ? <FormControlLabel control={<Switch
                checked={sendWelcomeMessage}
                onChange={onSendWelcomeMessageChange}
            />} label={i18n.translate('createEditUserModal.sendWelcomeMessage')}/> : ''}

            {sendWelcomeMessage ? <MessageTypeSelectionButtons
                    // multiple={true}
                    onSelectedMessageTypeChange={onSelectedMessagingTypeChange}
                    selectedMessagingTypes={selectedMessagingTypes}
                    // excludeMessageTypes={[MessageTypes.WHATSAPP]}
                />
                : ''}
        </div>
        <DataTable
            columns={[
                { key: 'firstName', label: i18n.translate('importUsersModal.users.firstName'), width: 150 },
                { key: 'lastName', label: i18n.translate('importUsersModal.users.lastName'), width: 150 },
                {
                    key: 'phone',
                    label: i18n.translate('importUsersModal.users.phone'),
                    width: 170,
                    render: ({row}) => {
                        const phone = get(row, 'phone', '');
                        return <PhoneCell value={formatPhoneForDisplay({phone, countryCode: companyContext.countryCode})} />;
                    }
                },
                { key: 'email', label: i18n.translate('importUsersModal.users.email'), width: 300 },
                { key: 'idNumber', label: i18n.translate('importUsersModal.users.idNumber'), width: 120 },
                { key: 'employeeId', label: i18n.translate('importUsersModal.users.employeeId'), width: 120 },
                {
                    key: 'organizations',
                    label: i18n.translate('importUsersModal.users.organizations'),
                    width: 300,
                    render: ({row}) => {
                        let organizations = get(row, 'organizations', '');
                        if (!organizations || organizations.trim() === '') {
                            return '-';
                        }
                        return <ScrollableListCell value={organizations} />;
                    }
                }
            ]}
            data={usersToUpload}
            errors={errors}
            showIndex={true}
            className="usersContainer"
        />

    </div>
}